// What an owner can do to a test after launching it, and why not when they can't.
//
// The server enforces these rules (close, PATCH and DELETE on a quiz in the
// API's organizations router); this mirror only decides what the console
// offers, so an owner sees a reason up front instead of an error afterwards.
// Keep the two in step:
//
//   * name and close time: always;
//   * open time: only while the test is upcoming;
//   * time limit and allowed attempts: only while nobody has started it;
//   * close now: only while it is open;
//   * delete: only while nobody has started it.

import { resolveQuizState, QUIZ_STATE } from './quizState';

export const UNLIMITED_ATTEMPTS = -1;

export const REASONS = {
  opened: 'The test has already opened, so its open time is fixed.',
  started: 'Students have already started this test, so it keeps the rules they started under.',
  checking: 'Checking whether anyone has started this test…',
  notOpenYet: 'This test has not opened yet. Delete it, or change when it opens.',
  alreadyClosed: 'This test is already closed.',
  startedNoDelete: 'Students have already started this test, so it cannot be deleted. Close it instead.',
};

const allow = () => ({ allowed: true, reason: null });
const deny = (reason) => ({ allowed: false, reason });

/**
 * Attempts across the results page's student rows, or null when there are no
 * rows to count. Every student with an attempt has a row, including ones who
 * have since left the batch, so the sum is every attempt at the test.
 */
export function totalAttempts(students) {
  if (!Array.isArray(students)) return null;
  return students.reduce((sum, s) => sum + (Number(s?.attemptCount) || 0), 0);
}

/**
 * Whether anyone has started the test: true, false, or null when unknown.
 * Nobody can start a test before it opens, so an upcoming one needs no count.
 */
export function hasStarted(state, attemptCount) {
  if (state === QUIZ_STATE.UPCOMING) return false;
  if (attemptCount == null) return null;
  return attemptCount > 0;
}

/**
 * Which lifecycle actions are on offer for a quiz, each as { allowed, reason }.
 *
 * `attemptCount` is null when the caller has not counted attempts (the test
 * list); anything that depends on it is then withheld rather than guessed.
 */
export function quizActions(quiz, { attemptCount = null, now = new Date() } = {}) {
  const state = resolveQuizState(quiz, now);
  const started = hasStarted(state, attemptCount);
  const unstartedOnly = (startedReason) => {
    if (started === false) return allow();
    return deny(started ? startedReason : REASONS.checking);
  };

  let close = allow();
  if (state === QUIZ_STATE.UPCOMING) close = deny(REASONS.notOpenYet);
  if (state === QUIZ_STATE.CLOSED) close = deny(REASONS.alreadyClosed);

  return {
    state,
    started,
    close,
    remove: unstartedOnly(REASONS.startedNoDelete),
    edit: {
      name: allow(),
      closesAt: allow(),
      opensAt: state === QUIZ_STATE.UPCOMING ? allow() : deny(REASONS.opened),
      durationMinutes: unstartedOnly(REASONS.started),
      allowedAttempts: unstartedOnly(REASONS.started),
    },
  };
}

/** A datetime-local input value ('YYYY-MM-DDTHH:mm', local time), or '' for none. */
export function toLocalInput(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The edit form's starting values for a quiz, as input strings. */
export function quizEditForm(quiz) {
  return {
    name: quiz?.name || '',
    opensAt: toLocalInput(quiz?.opensAt),
    closesAt: toLocalInput(quiz?.closesAt),
    durationMinutes: quiz?.durationMinutes ? String(quiz.durationMinutes) : '',
    allowedAttempts: String(quiz?.allowedAttempts ?? 1),
  };
}

/** Choices for the allowed-attempts select, keeping an unusual current value. */
export function attemptOptions(current) {
  const options = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: String(UNLIMITED_ATTEMPTS), label: 'Unlimited' },
  ];
  const value = String(current ?? 1);
  if (!options.some((o) => o.value === value)) options.splice(2, 0, { value, label: value });
  return options;
}

/**
 * The PATCH body for an edit: only what the owner actually changed.
 *
 * Untouched fields are left out rather than sent back. A datetime-local value
 * has minute precision, so an untouched open time sent back would differ from
 * the stored one and be refused as a change once the test has opened.
 */
export function buildQuizPatch(initial, form) {
  const patch = {};
  if (form.name.trim() !== initial.name.trim()) patch.name = form.name.trim();
  if (form.opensAt !== initial.opensAt) {
    patch.opensAt = form.opensAt ? new Date(form.opensAt).toISOString() : null;
  }
  if (form.closesAt !== initial.closesAt) {
    patch.closesAt = form.closesAt ? new Date(form.closesAt).toISOString() : null;
  }
  if (String(form.durationMinutes) !== String(initial.durationMinutes)) {
    patch.durationMinutes = Number(form.durationMinutes);
  }
  if (String(form.allowedAttempts) !== String(initial.allowedAttempts)) {
    patch.allowedAttempts = Number(form.allowedAttempts);
  }
  return patch;
}

/** What is wrong with an edit, in the owner's terms, or null if nothing. */
export function validateQuizEdit(initial, form) {
  if (!form.name.trim()) return 'Give the test a name.';
  // Only a changed time limit is checked, so a legacy test without one can
  // still be renamed.
  if (String(form.durationMinutes) !== String(initial.durationMinutes)) {
    const minutes = Number(form.durationMinutes);
    if (!form.durationMinutes || !Number.isInteger(minutes) || minutes < 1 || minutes > 600) {
      return 'Set a time limit between 1 and 600 minutes.';
    }
  }
  // Like the server, the window is checked as it will be after the edit, and
  // only when one of its ends moves.
  const windowMoved = form.opensAt !== initial.opensAt || form.closesAt !== initial.closesAt;
  if (windowMoved && form.opensAt && form.closesAt
      && new Date(form.closesAt) <= new Date(form.opensAt)) {
    return 'The close time must be after the open time.';
  }
  return null;
}
